import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Search, Inbox } from "lucide-react";
import { toast } from "sonner";

export default function CrudPage({
  title, subtitle, endpoint, columns, fields, canWrite = false,
  scope = null, onChanged, searchKeys = [], emptyHint = "Belum ada data.",
  headerActions = null, reloadSignal = 0,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [scopeVal, setScopeVal] = useState(scope?.options?.[0]?.value || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = scope && scopeVal ? { [scope.key]: scopeVal } : {};
      const res = await api.get(endpoint, { params });
      setRows(res.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [endpoint, scope, scopeVal]);

  useEffect(() => {
    load();
  }, [load, reloadSignal]);

  const openCreate = () => {
    const init = {};
    fields.forEach((f) => {
      init[f.key] = f.type === "switch" ? false : f.defaultValue !== undefined ? f.defaultValue : "";
    });
    if (scope && scopeVal) init[scope.key] = scopeVal;
    setForm(init);
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    const init = {};
    fields.forEach((f) => (init[f.key] = row[f.key] ?? (f.type === "switch" ? false : "")));
    if (scope) init[scope.key] = row[scope.key];
    setForm(init);
    setEditing(row);
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
      });
      if (scope) payload[scope.key] = scopeVal || form[scope.key];
      if (editing) {
        await api.put(`${endpoint}/${editing.id}`, payload);
        toast.success("Data berhasil diperbarui");
      } else {
        await api.post(endpoint, payload);
        toast.success("Data berhasil ditambahkan");
      }
      setDialogOpen(false);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    try {
      await api.delete(`${endpoint}/${row.id}`);
      toast.success("Data dihapus");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (!search) return true;
      const keys = searchKeys.length ? searchKeys : columns.map((c) => c.key).filter(Boolean);
      return keys.some((k) => String(r[k] ?? "").toLowerCase().includes(search.toLowerCase()));
    });
  }, [rows, search, searchKeys, columns]);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">{title}</h2>
          {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
        </div>
        {canWrite && (
          <div className="flex items-center gap-2 flex-wrap">
            {headerActions}
            <Button onClick={openCreate} data-testid="crud-add-btn" className="bg-green-700 hover:bg-green-800">
              <Plus className="h-4 w-4 mr-1.5" /> Tambah Data
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Cari..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="crud-search-input"
          />
        </div>
        {scope && (
          <Select value={scopeVal} onValueChange={setScopeVal}>
            <SelectTrigger className="w-full sm:w-72" data-testid="crud-scope-select">
              <SelectValue placeholder={scope.placeholder || "Pilih"} />
            </SelectTrigger>
            <SelectContent>
              {scope.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {columns.map((c) => (
                  <TableHead key={c.key || c.label} className={c.align === "right" ? "text-right" : ""}>
                    {c.label}
                  </TableHead>
                ))}
                {canWrite && <TableHead className="text-right w-24">Aksi</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-10 text-slate-400">Memuat...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} className="text-center py-12">
                    <Inbox className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">{emptyHint}</p>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id} className="hover:bg-slate-50">
                    {columns.map((c) => (
                      <TableCell key={c.key || c.label} className={c.align === "right" ? "text-right tabular-nums" : ""}>
                        {c.render ? c.render(row) : row[c.key]}
                      </TableCell>
                    ))}
                    {canWrite && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row)} data-testid={`edit-btn-${row.id}`}>
                            <Pencil className="h-4 w-4 text-slate-500" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`delete-btn-${row.id}`}>
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus data ini?</AlertDialogTitle>
                                <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => remove(row)} data-testid="confirm-delete-btn">Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Data" : "Tambah Data"} — {title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.filter((f) => !f.hidden).map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
                <Label className="text-xs font-semibold text-slate-600">{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={String(form[f.key] ?? "")} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger data-testid={`field-${f.key}`}><SelectValue placeholder="Pilih" /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((o) => (
                        <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : f.type === "switch" ? (
                  <div className="flex items-center gap-2 h-9">
                    <Switch checked={!!form[f.key]} onCheckedChange={(v) => setForm({ ...form, [f.key]: v })} data-testid={`field-${f.key}`} />
                    <span className="text-sm text-slate-600">{form[f.key] ? "Ya" : "Tidak"}</span>
                  </div>
                ) : f.type === "textarea" ? (
                  <Textarea value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    step={f.step}
                    min={f.type === "number" ? 0 : undefined}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    data-testid={`field-${f.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={saving} className="bg-green-700 hover:bg-green-800" data-testid="crud-save-btn">
              {saving ? "Menyimpan..." : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
